import { useCallback, useRef, useState } from "react"

import {
  Box,
  Button,
  Text,
  useDisclosure,
  FormLabel,
  Select,
  RadioGroup,
  Stack,
  Radio,
  useToast,
} from "@chakra-ui/react"
import format from "date-fns/format"

import DatePicker from "components/custom-input/date-picker"
import CustomModal from "components/custom-modal/custom-modal"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"

type TagButtonProps = {
  // eslint-disable-next-line react/no-unused-prop-types, react-redux/no-unused-prop-types
  children: string
  pmcId: string
  isDisabled: boolean
  checkedProperties: string[]
  resetToDefault: () => void
}
const TagButton = ({
  pmcId,
  isDisabled,
  checkedProperties,
  resetToDefault,
}: TagButtonProps) => {
  const displayToast = useToastHook()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const toast = useToast()
  const toastIdRef = useRef<number | string>("")

  const [selectedFromDate, setSelectedFromDate] = useState<Date>(new Date())
  const [selectedToDate, setSelectedToDate] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth())),
  )
  const [propertyCustomTag, setPropertyCustomTag] = useState<string>("tempHold")
  const [buttonType, setButtonType] = useState("apply")

  const addingTag = useCallback(
    (buttontype: string, propertyCustomTags: string, ids: string[]) => {
      toastIdRef.current = toast({
        description: buttontype === "apply" ? "Adding Tag" : "Removing Tag",
        status: "loading",
        position: "top-right",
        duration: 60 * 3 * 1000,
      })

      let result
      if (propertyCustomTags === "tempHold") {
        result = "Temp Hold"
      } else if (propertyCustomTags === "snooze") {
        result = "Snooze"
      } else if (propertyCustomTags === "inDataValidation") {
        result = "In Data Validation"
      } else if (propertyCustomTags === "doNotModify") {
        result = "Do Not Modify"
      } else if (propertyCustomTags === "tripOnly") {
        result = "Trip Only"
      } else if (propertyCustomTags === "investigatingPricing") {
        result = "Investigating Pricing"
      } else if (propertyCustomTags === "knownHighPricing") {
        result = "Known High Pricing"
      } else if (propertyCustomTags === "awaitingPmc") {
        result = "Awaiting PMC"
      } else if (propertyCustomTags === "awaitingPms") {
        result = "Awaiting PMS"
      } else if (propertyCustomTags === "dupe") {
        result = "Dupe"
      } else if (propertyCustomTags === "securityDeposit") {
        result = "Security Deposit Required"
      } else if (propertyCustomTags === "sharedSpace") {
        result = "Shared Space"
      } else if (propertyCustomTags === "pendingDnm") {
        result = "Pending DNM"
      } else if (propertyCustomTags === "suppressBadge") {
        result = "No Badge"
      } else if (propertyCustomTags === "taExclude") {
        result = "TA Exclude"
      } else {
        result = "Engineering Fixing Data Issues"
      }

      const newValues = {
        pmcId,
        tagName: result,
        ids,
        propertyCustomTags: {
          snooze: {
            startDate:
              propertyCustomTags === "snooze" && buttontype === "apply"
                ? format(selectedFromDate, "yyyy-MM-dd")
                : format(
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    "yyyy-MM-dd",
                  ),
            endDate:
              propertyCustomTags === "snooze" && buttontype === "apply"
                ? format(selectedToDate, "yyyy-MM-dd")
                : format(
                    new Date(Date.now() - 29 * 24 * 60 * 60 * 1000),
                    "yyyy-MM-dd",
                  ),
          },
          tempHold: buttontype === "apply" && propertyCustomTags === "tempHold",
          inDataValidation:
            buttontype === "apply" && propertyCustomTags === "inDataValidation",
          engineeringFixingDataIssues:
            buttontype === "apply" &&
            propertyCustomTags === "engineeringFixingDataIssues",
          doNotModify:
            buttontype === "apply" && propertyCustomTags === "doNotModify",
          tripOnly: buttontype === "apply" && propertyCustomTags === "tripOnly",
          investigatingPricing:
            buttontype === "apply" &&
            propertyCustomTags === "investigatingPricing",
          knownHighPricing:
            buttontype === "apply" && propertyCustomTags === "knownHighPricing",
          awaitingPmc:
            buttontype === "apply" && propertyCustomTags === "awaitingPmc",
          awaitingPms:
            buttontype === "apply" && propertyCustomTags === "awaitingPms",
          dupe: buttontype === "apply" && propertyCustomTags === "dupe",
          securityDeposit:
            buttontype === "apply" && propertyCustomTags === "securityDeposit",
          sharedSpace:
            buttontype === "apply" && propertyCustomTags === "sharedSpace",
          pendingDnm:
            buttontype === "apply" && propertyCustomTags === "pendingDnm",
          suppressBadge:
            buttontype === "apply" && propertyCustomTags === "suppressBadge",
          taExclude:
            buttontype === "apply" && propertyCustomTags === "taExclude",
        },
      }

      apiClient
        .addingTag({
          data: newValues,
        })
        .then(() => {
          resetToDefault()

          if (toastIdRef.current) {
            toast.update(toastIdRef.current, {
              description:
                buttontype === "apply"
                  ? `Tag Added to the Properties`
                  : `Tag Removed from the Properties`,
              status: "success",
              position: "top-right",
              duration: 3000,
            })
          }

          return true
        })
        .catch(() => {
          if (toastIdRef.current) {
            toast.update(toastIdRef.current, {
              description: `Faild to add tag to the Properties`,
              status: "error",
              position: "top-right",
              duration: 3000,
            })
          }
          displayToast({
            message: "Something went wrong! Can not add tag to the property.",
          })
        })
    },
    [
      pmcId,
      displayToast,
      toast,
      resetToDefault,
      selectedFromDate,
      selectedToDate,
    ],
  )

  const onSubmit = async () => {
    addingTag(buttonType, propertyCustomTag, checkedProperties)
    onClose()
  }

  return (
    <>
      <Button
        size='sm'
        variant='secondary'
        onClick={() => {
          setPropertyCustomTag("tempHold")
          onOpen()
        }}
        isDisabled={isDisabled}
      >
        Tags
      </Button>

      <CustomModal isOpen={isOpen} onClose={onClose} width='400px'>
        <Box>
          <Box p='10px'>
            <RadioGroup mb='20px' onChange={setButtonType} value={buttonType}>
              <Stack direction='row'>
                <Radio value='apply'>Apply Tag</Radio>
                <Radio value='remove'>Remove Tag</Radio>
              </Stack>
            </RadioGroup>
            <Box>
              <FormLabel htmlFor='userRole'>
                <Text size='sm' fontWeight='500'>
                  Tag
                </Text>
              </FormLabel>

              <Select
                onChange={(e) => setPropertyCustomTag(e.target.value)}
                id='propertyCustomTags'
                outlineOffset='0px'
                outlineColor='#e1e1e1'
              >
                <option value='tempHold'>Temp Hold</option>
                <option value='snooze'>Snooze</option>
                <option value='inDataValidation'>In Data Validation</option>
                <option value='engineeringFixingDataIssues'>
                  Engineering Fixing Data Issue
                </option>
                <option value='doNotModify'>Do Not Modify</option>
                <option value='tripOnly'>Trip Only</option>
                <option value='investigatingPricing'>
                  Investigating Pricing
                </option>
                <option value='knownHighPricing'>Known High Pricing</option>
                <option value='awaitingPmc'>Awaiting PMC</option>
                <option value='awaitingPms'>Awaiting PMS</option>
                <option value='dupe'>Dupe</option>
                <option value='securityDeposit'>
                  Security Deposit Required
                </option>
                <option value='sharedSpace'>Shared Space</option>
                <option value='pendingDnm'>Pending DNM</option>
                <option value='suppressBadge'>No Badge</option>
                <option value='taExclude'>TA Exclude</option>
              </Select>
            </Box>
            <Box
              mt='20px'
              display={
                propertyCustomTag === "tempHold" ||
                propertyCustomTag === "inDataValidation" ||
                propertyCustomTag === "engineeringFixingDataIssues" ||
                propertyCustomTag === "doNotModify" ||
                propertyCustomTag === "tripOnly" ||
                propertyCustomTag === "investigatingPricing" ||
                propertyCustomTag === "knownHighPricing" ||
                propertyCustomTag === "awaitingPmc" ||
                propertyCustomTag === "awaitingPms" ||
                propertyCustomTag === "dupe" ||
                propertyCustomTag === "securityDeposit" ||
                propertyCustomTag === "sharedSpace" ||
                propertyCustomTag === "pendingDnm" ||
                propertyCustomTag === "suppressBadge" ||
                propertyCustomTag === "taExclude" ||
                buttonType === "remove"
                  ? "none"
                  : "flex"
              }
              w={{ base: "full", md: "auto" }}
            >
              <Box width={{ base: "full", md: "165px", xl: "179px" }}>
                <DatePicker
                  id='name'
                  placeholder='From date'
                  label=''
                  error={undefined}
                  selectedDate={selectedFromDate}
                  maxDate={selectedToDate}
                  setSelectedDate={(date) => {
                    if (date) setSelectedFromDate(date)
                  }}
                />
              </Box>
              <Box
                width={{ base: "full", md: "165px", xl: "179px" }}
                ml={{ base: "10px", md: "20px" }}
              >
                <DatePicker
                  id='name'
                  placeholder='To date'
                  label=''
                  error={undefined}
                  selectedDate={selectedToDate}
                  minDate={selectedFromDate}
                  setSelectedDate={(date) => {
                    if (date) setSelectedToDate(date)
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            px='10px'
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Button
              width='100%'
              height='50px'
              marginY={{ base: "0px", sm: "16px" }}
              // marginRight='20px'
              type='submit'
              onClick={() => onSubmit()}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </CustomModal>
    </>
  )
}

export default TagButton
